import React, { useState, useRef } from "react";
import { Box } from "@mui/material";
import {
  VerificationCodeContainer,
  VerificationCodeField,
  VerificationCodeDivider,
} from "./VerificationCodeInput.styled";

interface VerificationCodeInputProps {
  length?: number;
  onComplete?: (code: string) => void;
  onChange?: (code: string) => void;
  error?: boolean;
  helperText?: string;
}

const VerificationCodeInput: React.FC<VerificationCodeInputProps> = ({
  length = 6,
  onComplete,
  onChange,
  error = false,
  helperText,
}) => {
  const [values, setValues] = useState<string[]>(new Array(length).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) return;

    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newValues = [...values];
    newValues[index] = value;
    setValues(newValues);

    const code = newValues.join("");
    onChange?.(code);

    // Auto-focus next input
    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if code is complete
    if (code.length === length && !code.includes("")) {
      onComplete?.(code);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === "Backspace" && !values[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");

    if (pastedData.length <= length) {
      const newValues = new Array(length).fill("");
      for (let i = 0; i < pastedData.length; i++) {
        newValues[i] = pastedData[i];
      }
      setValues(newValues);

      const code = newValues.join("");
      onChange?.(code);

      if (code.length === length) {
        onComplete?.(code);
      }
    }
  };

  return (
    <Box>
      <VerificationCodeContainer>
        {values.map((value, index) => (
          <React.Fragment key={index}>
            <VerificationCodeField
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={value}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              error={error}
              className={value === "" ? "empty-input" : ""}
              inputProps={{
                maxLength: 1,
                style: { textAlign: "center" },
              }}
              placeholder="0"
            />
            {index === 2 && <VerificationCodeDivider />}
          </React.Fragment>
        ))}
      </VerificationCodeContainer>
      {helperText && (
        <Box sx={{ mt: 1, textAlign: "center" }}>
          <Box
            component="span"
            sx={{
              color: error ? "error.main" : "text.secondary",
              fontSize: "14px",
            }}
          >
            {helperText}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VerificationCodeInput;
